import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';

import { Icon } from 'components/ui';
import { routes, TYPE_ICONS, TYPES } from 'constants/config';
import DateFormat from 'constants/date_format';
import { pluralizeWithCount } from 'utils/string_utils';
import request from 'utils/request_ajax';
import CertificationLearnersModal from './CertificationLearnersModal';
import CertificationOverviewHeaderCollapsible from './CertificationOverviewHeaderCollapsible';

const countContentTypes = (contents, typeCounts = {}) => {
  let counts = { ...typeCounts };
  const TYPE_TO_LABELS = {
    [TYPES.DEMONSTRATION]: {
      singular: t('common.demonstration'),
      plural: t('common.demonstration_plural'),
    },
    [TYPES.LESSON]: {
      singular: t('common.lesson'),
      plural: t('common.lesson_plural'),
    },
  };

  contents.map(content => {
    if (content.type === TYPES.PATH) {
      counts = countContentTypes(content.contents, counts);
      return counts;
    }
    if (counts[content.type]) {
      counts[content.type].count += 1;
      return counts;
    }
    counts[content.type] = {
      type: content.type,
      count: 1,
      label: TYPE_TO_LABELS[content.type],
    };
    return counts;
  });
  return counts;
};

const DetailItem = ({ iconName, detailText }) => (
  <div className="d-f ai-c c-brand-gray-dark CertificationOverviewHeader-detail-item">
    <Icon name={iconName} className="mr-8" />
    {detailText}
  </div>
);

function CertificationOverviewDetailsBlock({
  assignment,
  estimatedMinutesToComplete,
  contents,
  description,
  certificationId,
  pathId,
}) {
  const [showLearnerModal, setShowLearnerModal] = useState(false);
  const [
    otherLearnersTakingCertification,
    setOtherLearnersTakingCertification,
  ] = useState([]);
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const date = assignment.dueDate && moment(assignment.dueDate);
  const formattedDate = date && date.format(DateFormat.MONTH_DAY_YEAR_SHORT);
  const estimatedHoursToComplete =
    estimatedMinutesToComplete &&
    Math.round(10 * (estimatedMinutesToComplete / 60)) / 10;
  const counts = Object.values(countContentTypes(contents)).sort(
    (a, b) => b.count - a.count,
  );

  useEffect(() => {
    if (pathId) {
      setIsLoading(true);
      request
        .get(routes.OTHER_CERTIFICATION_LEARNERS(pathId), {
          page,
        })
        .then(data => {
          setOtherLearnersTakingCertification(otherLearners => {
            return [...otherLearners, ...data.otherLearnersTakingCertification];
          });
          setTotalUsers(data.totalUsers);
          setIsLoading(false);
        });
    }
  }, [page, pathId]);

  const toggleLearnerModal = () => {
    setShowLearnerModal(!showLearnerModal);
  };

  return (
    <CertificationOverviewHeaderCollapsible>
      <div className="pl-32 pr-32 jc-sb CertificationOverviewHeader-details-container">
        {description && (
          <div className="c-brand-gray-darkest mt-24 fz-16 f-2 lh-24 CertificationOverviewHeader-description">
            {description}
          </div>
        )}
        <div
          className={classNames(
            'd-f fd-c f-1 mt-24 CertificationOverviewHeader-details',
            {
              'CertificationOverviewHeader-details--no-description':
                !!description,
            },
          )}
        >
          {formattedDate && (
            <DetailItem
              iconName="calendar"
              detailText={
                <time
                  dateTime={date.toJSON()}
                  title={date.format(DateFormat.LONG)} // tooltip
                >
                  {t('assignments.due')} {formattedDate}
                </time>
              }
            />
          )}
          {counts.map(({ type, count, label }) => (
            <DetailItem
              key={type}
              iconName={TYPE_ICONS[type]}
              detailText={pluralizeWithCount(
                count,
                label.singular,
                label.plural,
              )}
            />
          ))}
          {estimatedHoursToComplete && (
            <DetailItem
              iconName="clock"
              detailText={t('common.hours', {
                count: estimatedHoursToComplete,
              })}
            />
          )}
          {totalUsers > 0 && (
            <button
              type="button"
              className="u-linkBtn fz-16 lh-24 c-brand-blue ta-l"
              onClick={toggleLearnerModal}
            >
              {t('certifications.learner_overview.view_all_learners_button')}
            </button>
          )}
        </div>
        {showLearnerModal && (
          <CertificationLearnersModal
            closeModal={toggleLearnerModal}
            certificationId={certificationId}
            isLoading={isLoading}
            otherLearnersTakingCertification={otherLearnersTakingCertification}
            totalUsers={totalUsers}
            setPage={setPage}
            page={page}
          />
        )}
      </div>
    </CertificationOverviewHeaderCollapsible>
  );
}

const mapStateToProps = ({ pathViewer }) => ({
  pathId: pathViewer.pathId,
});

export default connect(
  mapStateToProps,
  null,
)(CertificationOverviewDetailsBlock);
